import { Link } from 'react-router';
import { Alert } from '../../../ui/feedback';
import { Timestamp } from '../../../ui/Timestamp';
import type { ReleaseDetail } from '../types';
import { UnlinkedRunAlert } from '../UnlinkedRuns';

export function ReleaseStatus({ r, base }: { r: ReleaseDetail; base: string }) {
  const latest = r.attempts[0];
  return (
    <>
      {r.problems.length > 0 && (
        <Alert tone="warning" title="This release cannot proceed as is">
          <ul>
            {r.problems.map((p, i) => (
              <li key={i}>{p.message}</li>
            ))}
          </ul>
        </Alert>
      )}
      {(r.state === 'deploying' || r.state === 'unknown') && latest?.awaitingVerification && (
        <Alert tone="info" title="Waiting for manual checks">
          <span>
            The{' '}
            <a href={latest.run!.url} target="_blank" rel="noreferrer">
              Deploy run
            </a>{' '}
            succeeded. An admin records the release&apos;s manual checks on ATT-{latest.number}; it succeeds
            when they all pass. <Link to={`${base}/attempts/ATT-${latest.number}`}>Record checks</Link>
          </span>
        </Alert>
      )}
      {(r.state === 'deploying' || r.state === 'unknown') && latest?.needsReport && (
        <UnlinkedRunAlert
          attempt={latest.number}
          executor={latest.executor?.name ?? null}
          runs={latest.unlinkedRuns}
          link={`${base}/attempts/ATT-${latest.number}`}
        />
      )}
      {(r.state === 'deploying' || r.state === 'unknown') &&
        latest &&
        !latest.awaitingVerification &&
        !latest.needsReport && (
          <Alert
            tone={r.state === 'unknown' ? 'warning' : 'info'}
            title={r.state === 'unknown' ? 'Outcome unknown — environment locked' : 'Deployment in progress'}
          >
            <span>
              Bindings and included scopes are locked until ATT-{latest.number} is reconciled. Editing tickets
              cannot undo external execution.{' '}
              <Link to={`${base}/attempts/ATT-${latest.number}`}>Open ATT-{latest.number}</Link>
            </span>
          </Alert>
        )}
      {r.state === 'failed' && (
        <Alert tone="error" title="Last attempt failed">
          Tickets stay Pending release and remain reserved. Merge a fix and proceed again
          {r.environment.requiresApproval ? ' after preparing a retry and a fresh approval' : ''}, or cancel
          the release.
        </Alert>
      )}
      {r.state === 'awaiting_approval' && (
        <Alert tone="info">
          Waiting for an admin to review approval {r.approvals[0]?.displayId}.{' '}
          <Link to={`/approvals/${r.approvals[0]?.displayId}`}>Open request</Link>
        </Alert>
      )}
      {r.viewer.approvalToUse && (
        <Alert tone="success" title="Approved for one attempt">
          Proceed before <Timestamp value={r.viewer.approvalToUse.useBy} /> to use{' '}
          {r.viewer.approvalToUse.displayId}. A retry needs a new approval.
        </Alert>
      )}
    </>
  );
}
